import ExcelJS from 'exceljs'
import { entries } from 'lodash'
import { DEVIATION_ANALYSIS_LABELS, DeviationAnalysisRowLabelKey } from './constants'
import { DeviationValueType, TransformedDeviationAnalysisType, TransformedDeviationSchema, ValuationDeviationOption } from './types'
import { isKeyOf } from 'ComponentLibrary/src/common/type-predicates'
import FileSaver from 'file-saver'
import { isoDateStringToLocalFormat } from 'ComponentLibrary/src/common/date'
import { formatExcelCell } from 'ComponentLibrary/src/common/excel'

const STARTING_COLUMN_INDEX = 1
const STARTING_ROW_INDEX = 1

export type DeviationAnalysisExportCopy = {
    fileNamePrefix: string
    worksheetName: string
    assetLabel: string
    valueLabel: string
    changeLabel: string
    changePercentageLabel: string
    remainingUsefulLifePrefix: string
    rowLabels: Partial<Record<DeviationAnalysisRowLabelKey, string>>
    percentagePointUnit?: string
    customRowLabels?: Record<string, string>
}

const DEFAULT_DEVIATION_ANALYSIS_EXPORT_COPY: DeviationAnalysisExportCopy = {
    fileNamePrefix: 'Abweichungsanalyse',
    worksheetName: 'Abweichungsanalyse',
    assetLabel: 'Immobilie',
    valueLabel: 'Wert',
    changeLabel: 'Veränderung',
    changePercentageLabel: 'Veränderung %',
    remainingUsefulLifePrefix: 'Restnutzungsdauer',
    rowLabels: DEVIATION_ANALYSIS_LABELS,
    percentagePointUnit: 'BP',
    customRowLabels: {},
}

function processDeviation<T extends DeviationValueType>(
    worksheet: ExcelJS.Worksheet,
    rowIndex: number,
    rowLabel: string,
    deviationSection?: TransformedDeviationSchema<T>,
    percentagePointUnit = 'BP',
    locale = 'de-DE'
) {
    if (deviationSection !== undefined) {
        const row = worksheet.getRow(rowIndex)
        const labelCell = row.getCell(STARTING_COLUMN_INDEX)
        labelCell.value = rowLabel
        labelCell.font = { bold: true, size: 14 }

        const comparisonValuationCell = formatExcelCell(deviationSection.comparisonValuation, undefined, percentagePointUnit, locale)
        const baseValueCell = formatExcelCell(deviationSection.baseValue, undefined, percentagePointUnit, locale)
        const deviationCell = formatExcelCell(deviationSection.deviation, true, percentagePointUnit, locale)
        const deviationPercentageCell = formatExcelCell(deviationSection.deviationPercentage, undefined, percentagePointUnit, locale)

        const cells = [comparisonValuationCell, baseValueCell, deviationCell, deviationPercentageCell]
        cells.forEach((cellData, index) => {
            const cell = row.getCell(index + STARTING_COLUMN_INDEX + 1) // Start from column after label
            cell.value = cellData.value
            cell.alignment = { horizontal: 'right' }
            cell.font = { size: 14 }
            if (cellData.numFmt) {
                cell.numFmt = cellData.numFmt
            }
        })
    }
}

type TransformDeviationAnalysisToWorksheetProps = {
    workbook: ExcelJS.Workbook
    deviationAnalysis: TransformedDeviationAnalysisType
    assetTitle: string
    baseValuation?: ValuationDeviationOption
    comparisonValuation?: ValuationDeviationOption
    formattedBaseValuationDate?: string
    formattedComparisonValuationDate?: string
    copy: DeviationAnalysisExportCopy
    locale: string
}

function getDeviationRowLabel({
    sectionKey,
    deviationItemKey,
    deviationItem,
    copy,
}: {
    sectionKey: string
    deviationItemKey: string
    deviationItem: TransformedDeviationSchema<DeviationValueType>
    copy: DeviationAnalysisExportCopy
}) {
    const translatedRowLabel = copy.rowLabels[deviationItemKey as DeviationAnalysisRowLabelKey]
    const translatedCustomRowLabel = deviationItem.customLabel ? copy.customRowLabels?.[deviationItem.customLabel] : undefined
    let rowLabel =
        translatedCustomRowLabel ||
        deviationItem.customLabel ||
        translatedRowLabel ||
        (isKeyOf(deviationItemKey, DEVIATION_ANALYSIS_LABELS) ? DEVIATION_ANALYSIS_LABELS[deviationItemKey] : deviationItemKey)

    if (sectionKey === 'valuationStructureLifeSpans') {
        rowLabel = `${copy.remainingUsefulLifePrefix} ${rowLabel}`
    }

    return rowLabel
}

function transformDeviationAnalysisToWorksheet({
    workbook,
    deviationAnalysis,
    assetTitle,
    baseValuation,
    comparisonValuation,
    formattedBaseValuationDate,
    formattedComparisonValuationDate,
    copy,
    locale,
}: TransformDeviationAnalysisToWorksheetProps) {
    const worksheet = workbook.addWorksheet(copy.worksheetName)
    let currentRow = STARTING_ROW_INDEX

    for (let col = STARTING_COLUMN_INDEX; col < 6 + STARTING_COLUMN_INDEX; col++) {
        const column = worksheet.getColumn(col)
        column.width = 27
    }

    const assetRow = worksheet.getRow(currentRow)
    const assetLabelCell = assetRow.getCell(STARTING_COLUMN_INDEX)
    assetLabelCell.value = copy.assetLabel
    assetLabelCell.font = { bold: true, size: 14 }
    const assetValueCell = assetRow.getCell(STARTING_COLUMN_INDEX + 1)
    assetValueCell.value = assetTitle
    assetValueCell.font = { size: 14 }
    currentRow++
    currentRow++

    const headerCellGroups = [
        ['', formattedComparisonValuationDate, formattedBaseValuationDate],
        [copy.valueLabel, comparisonValuation?.title, baseValuation?.title, copy.changeLabel, copy.changePercentageLabel],
    ]
    headerCellGroups.forEach((headerCells) => {
        if (headerCells.some((header) => !!header)) {
            const headerRow = worksheet.getRow(currentRow)
            headerCells.forEach((headerLabel, index) => {
                const cell = headerRow.getCell(index + STARTING_COLUMN_INDEX)
                cell.value = headerLabel
                cell.font = { bold: true, size: 14 }
                cell.alignment = { horizontal: index === 0 ? 'left' : 'right' }
            })
            currentRow++
        }
    })

    // This is freaking ugly 😱
    entries(deviationAnalysis)
        .filter(([_, section]) => !!section)
        .forEach(([sectionKey, section]) => {
            entries(section)
                .filter(([_, deviationItem]) => !!deviationItem)
                .forEach(([deviationItemKey, deviationItem]) => {
                    const rowLabel = getDeviationRowLabel({
                        sectionKey,
                        deviationItemKey,
                        deviationItem,
                        copy,
                    })
                    processDeviation(worksheet, currentRow, rowLabel, deviationItem, copy.percentagePointUnit, locale)
                    currentRow++
                })
        })

    return worksheet
}

type ExportDeviationAnalysisToExcelProps = Omit<TransformDeviationAnalysisToWorksheetProps, 'workbook' | 'copy' | 'locale'> & {
    copy?: DeviationAnalysisExportCopy
    locale?: string
}

export async function exportDeviationAnalysisToExcel({
    deviationAnalysis,
    assetTitle,
    baseValuation,
    comparisonValuation,
    copy = DEFAULT_DEVIATION_ANALYSIS_EXPORT_COPY,
    locale = 'de-DE',
}: ExportDeviationAnalysisToExcelProps) {
    const formattedComparisonValuationDate = isoDateStringToLocalFormat(comparisonValuation?.date, false, locale)
    const formattedBaseValuationDate = isoDateStringToLocalFormat(baseValuation?.date, false, locale)

    const workbook = new ExcelJS.Workbook()
    const deviationTitle = `${assetTitle}-${formattedComparisonValuationDate}-${formattedBaseValuationDate}`.replace(/[\s.\\/]+/g, '-')

    transformDeviationAnalysisToWorksheet({
        workbook,
        deviationAnalysis,
        assetTitle,
        baseValuation,
        comparisonValuation,
        formattedComparisonValuationDate,
        formattedBaseValuationDate,
        copy,
        locale,
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

    FileSaver.saveAs(blob, `${copy.fileNamePrefix}-${deviationTitle}.xlsx`)
}
